import { useDocumentChangeListener } from "@/hooks/useDocumentChangeListener";
import { useDocumentCreationOnError } from "@/hooks/useDocumentCreationOnError";
import { useDocument } from "@/hooks/useDocument";
import * as React from "react";
import { LogOut, UserPlus } from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { useLogout } from "@/hooks/useLogout";
import { getDisplayName, getInitials, getProfilePicture } from "@/utils/user.utils";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { useSubscription } from "@/hooks/useSubscription";
import { Loader } from "lucide-react";
import { RequestGalleryProvider } from "./request-gallery/RequestGalleryContext";

import Discuss from "./Discuss";
const Research = React.lazy(() => import("./Research"));
const Review = React.lazy(() => import("./Review"));
const UpgradePlanDialog = React.lazy(() => import("./UpgradePlanDialog"));
const ReferUserDialog = React.lazy(() => import("./ReferUserDialog"));

enum AuthenticatedScreenTabs {
  REVIEW = "review",
  DISCUSS = "discuss",
  RESEARCH = "research",
}

const AuthenticatedScreen: React.FC = () => {
  const { user } = useUser();
  const logoutMutation = useLogout();
  const { data: subscription } = useSubscription({
    staleTime: 30000,
  });
  const { data: doc } = useDocument();
  const [selectedTab, setSelectedTab] = React.useState<AuthenticatedScreenTabs>(
    AuthenticatedScreenTabs.DISCUSS
  );
  const [hasUnreadDiscuss, setHasUnreadDiscuss] = React.useState(false);
  const [hasUnreadResearch, setHasUnreadResearch] = React.useState(false);
  const [hasUnreadReview, setHasUnreadReview] = React.useState(false);

  const [isReferUserDialogOpen, setIsReferUserDialogOpen] = React.useState(false);

  // Use ref to track selectedTab for callbacks to avoid stale closures
  const selectedTabRef = React.useRef(selectedTab);

  React.useEffect(() => {
    selectedTabRef.current = selectedTab;
  }, [selectedTab]);

  // Set up document change listener
  useDocumentChangeListener();

  // Handle 404 error and create document
  useDocumentCreationOnError();

  // Handle document cleanup on unload
  React.useEffect(() => {
    const handleCleanup = () => {
      if (doc?.upload_text_url) {
        // Use fetch with keepalive to ensure the request is sent even if the page closes
        fetch(doc.upload_text_url, {
          method: "PUT",
          body: "",
          keepalive: true,
          headers: {
            "Content-Type": "text/plain",
          },
        }).catch((err) => {
          console.error("Failed to clear document text on unload:", err);
        });
      }
    };

    window.addEventListener("beforeunload", handleCleanup);
    window.addEventListener("unload", handleCleanup);

    return () => {
      window.removeEventListener("beforeunload", handleCleanup);
      window.removeEventListener("unload", handleCleanup);
    };
  }, [doc?.upload_text_url]);

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleTabChange = (value: string) => {
    setSelectedTab(value as AuthenticatedScreenTabs);
    if (value === AuthenticatedScreenTabs.DISCUSS) {
      setHasUnreadDiscuss(false);
    }
    if (value === AuthenticatedScreenTabs.RESEARCH) {
      setHasUnreadResearch(false);
    }
    if (value === AuthenticatedScreenTabs.REVIEW) {
      setHasUnreadReview(false);
    }
  };

  const handleNewMessage = () => {
    // Switch to Discuss tab so the user sees the processing message
    if (selectedTabRef.current !== AuthenticatedScreenTabs.DISCUSS) {
      setSelectedTab(AuthenticatedScreenTabs.DISCUSS);
      setHasUnreadDiscuss(false);
    }
  };

  const handleResearchNewMessage = () => {
    if (selectedTabRef.current !== AuthenticatedScreenTabs.RESEARCH) {
      setHasUnreadResearch(true);
    }
  };

  const handleReviewComplete = () => {
    if (selectedTabRef.current !== AuthenticatedScreenTabs.REVIEW) {
      setHasUnreadReview(true);
    }
  };

  return (
    <RequestGalleryProvider>
      <div
        className={`flex flex-col h-full bg-background ${!subscription?.is_ms_word_license ? "pointer-events-none opacity-50" : ""}`}
      >
        {/* Topbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <div>
            <img src="assets/aila-logo-updated.png" alt="Logo" width="92" height="34" />
          </div>

          <div>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={getProfilePicture(user || null)!}
                    alt={getDisplayName(user || null)}
                  />
                  <AvatarFallback>{getInitials(user || null)}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" forceMount>
                <DropdownMenuItem onClick={() => setIsReferUserDialogOpen(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  <span>Refer a Friend</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} disabled={logoutMutation.isPending}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{logoutMutation.isPending ? "Logging out..." : "Logout"}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 py-2 flex-1 overflow-hidden">
          <Tabs
            value={selectedTab}
            onValueChange={handleTabChange}
            className="w-full h-full flex flex-col gap-3"
          >
            <TabsList className="grid w-full grid-cols-3 p-1.5 h-auto">
              <TabsTrigger className="h-9 text-xs relative" value={AuthenticatedScreenTabs.DISCUSS}>
                Discuss
                {hasUnreadDiscuss && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full z-50 border border-background" />
                )}
              </TabsTrigger>
              <TabsTrigger className="h-9 text-xs relative" value={AuthenticatedScreenTabs.REVIEW}>
                Review
                {hasUnreadReview && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full z-50 border border-background" />
                )}
              </TabsTrigger>
              <TabsTrigger
                className="h-9 text-xs relative"
                value={AuthenticatedScreenTabs.RESEARCH}
              >
                Research
                {hasUnreadResearch && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full z-50 border border-background" />
                )}
              </TabsTrigger>
            </TabsList>
            <React.Suspense
              fallback={
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <Loader className="animate-spin" size={48} />
                  <p>Loading...</p>
                </div>
              }
            >
              <TabsContent
                value={AuthenticatedScreenTabs.DISCUSS}
                className="flex-1 min-h-0 flex flex-col"
                forceMount
                hidden={selectedTab !== AuthenticatedScreenTabs.DISCUSS}
              >
                <Discuss onNewMessage={handleNewMessage} />
              </TabsContent>
              <TabsContent
                value={AuthenticatedScreenTabs.RESEARCH}
                className="flex-1 min-h-0 flex flex-col"
                forceMount
                hidden={selectedTab !== AuthenticatedScreenTabs.RESEARCH}
              >
                <Research onNewMessage={handleResearchNewMessage} />
              </TabsContent>
              <TabsContent
                value={AuthenticatedScreenTabs.REVIEW}
                className="flex-1 min-h-0 flex flex-col"
                forceMount
                hidden={selectedTab !== AuthenticatedScreenTabs.REVIEW}
              >
                <Review onReviewComplete={handleReviewComplete} />
              </TabsContent>
            </React.Suspense>
          </Tabs>
        </div>
      </div>
      <React.Suspense fallback={null}>
        <UpgradePlanDialog
          open={!!user && !subscription?.is_ms_word_license}
          onOpenChange={() => {}}
        />
        <ReferUserDialog open={isReferUserDialogOpen} onOpenChange={setIsReferUserDialogOpen} />
      </React.Suspense>
    </RequestGalleryProvider>
  );
};

export default AuthenticatedScreen;
